
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  withStyles,
  Grid,
  Card,
} from '@material-ui/core';

// actions
import {
  getEvent,
  patchEvent,
} from '@pingum/app-state-management/event/actions';
import { getStatuses } from '@pingum/app-state-management/status/actions';
import { getTags } from '@pingum/app-state-management/tag/actions';
import { getTagTypes } from '@pingum/app-state-management/tagType/actions';
import { getEventData } from '@pingum/app-state-management/eventData/actions';
import { getEventNotificationsForEvent } from '@pingum/app-state-management/eventNotification/actions';
import { UserService } from '@pingum/api-services';

// components
import Page from '../../widgets/Page';
import SuccessContainer from '../../widgets/SuccessContainer';
import ErrorContainer from '../../widgets/ErrorContainer';

// utils
import {
  isUserAdmin,
  isUserRestricted,
} from '../../../utils/tenantAuthenticationHelper';
import { getTenantFromUrl } from '../../../utils/urlHelper';
import EventDataCard from './EventDataCard';

import styles from './styles';

export class EventDetailPageComponent extends React.Component {
  constructor(props) {
    super(props);

    if (props.statuses.length === 0) {
      props.getStatuses();
    }

    if (props.tagTypes.length === 0) {
      props.getTagTypes();
    }

    if (props.tags.length === 0) {
      props.getTags();
    }

    if (props.eventData.length === 0) {
      props.getEventData();
    }

    const id = props.history.location.pathname.replace(/^\/events[/]?/, '').replace(/\//g, '');
    if (id && id !== props.selectedEvent.id) {
      props.getEvent(id);
      this.props.getEventNotificationsForEvent(id);
    }
  }

  componentDidUpdate(prevProps) {
    const urlId = this.props.history.location.pathname.replace(/^\/events[/]?/, '').replace(/\//g, '');
    const prevId = prevProps.selectedEvent.id;
    const currentId = this.props.selectedEvent.id;

    if (
      urlId
        && prevId
        && currentId
        && urlId !== currentId.toString()
    ) {
      this.props.getEvent(urlId);
      this.props.getEventNotificationsForEvent(urlId);
    }
  }

  render() {
    const {
      user,
      tenant,
      openInEditMode,
      selectedEvent,
      classes,
      isFetching,
      action,
      statuses,
      tags: propTags,
      tagTypes,
      eventData: propEventData,
      eventNotifications,
      isFetchingEventNotifications,
      patchEvent: propPatchEvent,
      canAttachFiles,
      hidePageWrapper,
      tenantUsers,
    } = this.props;

    const roles = UserService.getUserRolesForTenant(
      user,
      tenant,
    );
    const isAdmin = isUserAdmin(roles);
    const isRestricted = isUserRestricted(roles);

    const content = (
      <div>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <EventDataCard
              openInEditMode={openInEditMode}
              selectedEvent={selectedEvent}
              classess={classes}
              statuses={statuses}
              tags={propTags}
              tagTypes={tagTypes}
              eventData={propEventData}
              tenantUsers={tenantUsers}
              patchEvent={propPatchEvent}
              isAdmin={isAdmin}
              isRestricted={isRestricted}
              isFetching={isFetching}
              canAttachFiles={canAttachFiles}
              eventNotifications={eventNotifications}
              isFetchingEventNotifications={isFetchingEventNotifications}
            />
          </Grid>
        </Grid>

        {/* Snackbars cannot be seen over a dialog so we need to place it here */}
        <SuccessContainer />
        <ErrorContainer />
      </div>
    );

    return hidePageWrapper
      ? content
      : (
        <Page
          title="Event Detail"
          showLoadingIndicator={isFetching}
          disableToolbarGutters
          action={action}
          className={classes.container}
        >
          <Card>
            {content}
          </Card>
        </Page>
      );
  }
}

EventDetailPageComponent.propTypes = {
  user: PropTypes.object,
  tenant: PropTypes.string,
  openInEditMode: PropTypes.bool,
  action: PropTypes.any,
  selectedEvent: PropTypes.object,
  getEvent: PropTypes.func,
  history: PropTypes.object,
  classes: PropTypes.object,
  statuses: PropTypes.array,
  getStatuses: PropTypes.func,
  getTags: PropTypes.func,
  getTagTypes: PropTypes.func,
  getEventData: PropTypes.func,
  getEventNotificationsForEvent: PropTypes.func,
  eventNotifications: PropTypes.array,
  isFetchingEventNotifications: PropTypes.bool,
  isFetching: PropTypes.bool,
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  eventData: PropTypes.array,
  patchEvent: PropTypes.func,
  canAttachFiles: PropTypes.bool,
  hidePageWrapper: PropTypes.bool,
  tenantUsers: PropTypes.array,
};

EventDetailPageComponent.defaultProps = {
  user: { user_id: '' },
  tenant: undefined,
  action: null,
  openInEditMode: false,
  selectedEvent: {},
  getEvent: () => {},
  history: { location: { pathname: '' } },
  classes: {},
  statuses: [],
  getStatuses: () => {},
  getTags: () => {},
  getTagTypes: () => {},
  getEventData: () => {},
  eventNotifications: [],
  isFetchingEventNotifications: true,
  getEventNotificationsForEvent: () => {},
  isFetching: false,
  tags: [],
  tagTypes: [],
  eventData: [],
  patchEvent: () => {},
  canAttachFiles: false,
  hidePageWrapper: false,
  tenantUsers: [],
};

export const mapStateToProps = (state) => ({
  user: state.user.user,
  tenant: getTenantFromUrl(),
  selectedEvent: state.event.selectedEvent,
  statuses: state.status.statuses,
  isFetching:
    state.event.isFetchingEvent
    || state.status.isFetchingStatuses,
  tags: state.tag.tags,
  tagTypes: state.tagType.tagTypes,
  eventData: state.eventData.eventData,
  eventNotifications: state.eventNotification.eventNotifications,
  isFetchingEventNotifications: state.eventNotification.isFetchingEventNotifications,
  canAttachFiles: (state.tenant.tenant && state.tenant.tenant.stripePlan !== null),
  tenantUsers: state.tenant.tenantUsers,
});

export const mapDispatchToProps = (dispatch) => ({
  getEvent: (id) => dispatch(getEvent(id)),
  getStatuses: () => dispatch(getStatuses()),
  getTags: () => dispatch(getTags(false, false)),
  getTagTypes: () => dispatch(getTagTypes(false, false)),
  getEventData: () => dispatch(getEventData(false)),
  getEventNotificationsForEvent: (id) => dispatch(getEventNotificationsForEvent(id)),
  patchEvent: (id, request) => dispatch(patchEvent(id, request)),
});

export default withStyles(styles)(connect(
  mapStateToProps,
  mapDispatchToProps,
)(EventDetailPageComponent));
